import { useEffect, useMemo, useState } from 'react';
import { HttpTransportType, HubConnectionBuilder } from '@microsoft/signalr';

import { RFQApi } from '@setvi/shared/enums';

import { Bold } from '../styled';

/* enum STATUSES {
  NONE = '',
  RECOGNITION = 1,
  VECTOR_SEARCH = 3,
  MONGODB_SEARCH = 4,
  FIRST_PROCESSING = 5,
  SECOND_PROCESSING = 6
} */

const extractNumberFromMessage = (message: string) => {
  const match = message.match(/\b\d+\b/);

  return match ? parseInt(match[0], 10) : null;
};

export const useProductLoader = ({ statusId }: { statusId?: string }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [counter, setCounter] = useState<{ step: number; count: number }[]>([]);

  const loaderSteps = useMemo(
    () => [
      {
        key: 1,
        initial: <>Product search</>,
        progress: <>Preparing to search the product catalog database..</>,
        completed: (
          <>
            Searched{' '}
            <Bold>{counter.find(c => c.step === 0)?.count} products</Bold> in
            our catalog database
          </>
        )
      },
      {
        key: 2,
        initial: <>Match Detection</>,
        progress: <>Getting ready to analyze your preferences...</>,
        completed: (
          <>
            Found{' '}
            <Bold>{counter.find(c => c.step === 1)?.count} close matches</Bold>{' '}
            based on your preferences
          </>
        )
      },
      {
        key: 3,
        initial: <>Ranking results</>,
        progress: <>Setting ranking criteria...</>,
        completed: (
          <>
            Ranked results by{' '}
            <Bold>
              vendor priority, stocked products, private label strategy, in
              stock products etc..
            </Bold>
          </>
        )
      },
      {
        key: 4,
        initial: <>Find Recommendations</>,
        progress: <>Finalizing your top recommendations</>,
        completed: (
          <>
            Recommendation Reasoning:This match offers{' '}
            <Bold>the best price-performance ratio</Bold> among your options.
          </>
        )
      }
    ],
    [counter]
  );

  useEffect(() => {
    if (statusId) {
      const connection = new HubConnectionBuilder()
        .withUrl(
          `${process.env.INTELLIGENCE_API_BASE_URL}${RFQApi.GetJobStatus}`,
          {
            skipNegotiation: true,
            transport: HttpTransportType.WebSockets // Explicitly set transport to WebSockets
          }
        )
        .withAutomaticReconnect()
        .build();

      connection.start();

      connection.on('AiSearchProgressUpdate', signal => {
        if (signal) {
          const count = extractNumberFromMessage(signal?.message);
          const status = signal?.status;

          if (status >= 1 && status <= 3) {
            setCurrentStep(0);
            if (count) setCounter(prev => [...prev, { step: 0, count }]);
          } else if (status === 4) {
            setCompletedSteps([0]);
            setCurrentStep(1);
            setCounter(prev => [...prev, { step: 1, count }]);
          } else if (status === 5) {
            setCompletedSteps(prev => [...prev, 1]);
            setCurrentStep(2);

            // after 3 seconds set current step to 4
            setTimeout(() => {
              setCompletedSteps(prev => [...prev, 2]);
              setCurrentStep(3);
            }, 3000);
          } else if (status === 7) {
            setCompletedSteps(prev => [...prev, 3]);
          }
        }
      });

      return () => {
        connection.stop();
      };
    }

    return () => {};
  }, [statusId]);

  return {
    currentStep,
    completedSteps,
    loaderSteps
  };
};
